import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Home,
  Building,
  FileText,
  DollarSign,
  MessageSquare,
  UserCheck,
  Shield,
  Plus,
  Edit,
  Trash2,
  Search,
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../integrations/supabase/client";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";

interface Scholarship {
  id: string;
  name: string;
  description: string | null;
  eligibility_criteria: string | null;
  amount: number | null;
  application_deadline: string | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export default function Scholarships() {
  const { user } = useAuth();
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingScholarship, setEditingScholarship] =
    useState<Scholarship | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    eligibility_criteria: "",
    amount: "",
    application_deadline: "",
    status: "active",
  });

  const sidebarItems = [
    {
      label: "Dashboard",
      path: "/jainboarding-admin/dashboard/hostel-admin",
      icon: <Home className="h-5 w-5" />,
    },
    {
      label: "Hostels",
      path: "/jainboarding-admin/hostels",
      icon: <Building className="h-5 w-5" />,
    },
    {
      label: "Scholarships",
      path: "/jainboarding-admin/scholarships",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "Donation Status",
      path: "/jainboarding-admin/funding",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      label: "Reports",
      path: "/jainboarding-admin/reports",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "CMS",
      path: "/jainboarding-admin/cms",
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      label: "CRM",
      path: "/jainboarding-admin/crm",
      icon: <UserCheck className="h-5 w-5" />,
    },
    {
      label: "Settings",
      path: "/jainboarding-admin/settings",
      icon: <Shield className="h-5 w-5" />,
    },
  ];

  const fetchScholarships = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("scholarships")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }
      setScholarships(data ?? []);
    } catch (error: any) {
      console.error("Error fetching scholarships:", error);
      toast.error("Failed to load scholarships");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScholarships();
  }, [fetchScholarships]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const scholarshipData = {
        name: formData.name,
        description: formData.description || null,
        eligibility_criteria: formData.eligibility_criteria || null,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        application_deadline: formData.application_deadline || null,
        status: formData.status,
      };

      if (editingScholarship) {
        // Update existing scholarship
        const { error } = await supabase
          .from("scholarships")
          .update(scholarshipData)
          .eq("id", editingScholarship.id);

        if (error) {
          throw error;
        }
        toast.success("Scholarship updated successfully!");
      } else {
        // Create new scholarship
        const { error } = await supabase
          .from("scholarships")
          .insert([scholarshipData]);

        if (error) {
          throw error;
        }
        toast.success("Scholarship created successfully!");
      }

      fetchScholarships();
      handleCloseDialog();
    } catch (error: any) {
      console.error("Error saving scholarship:", error);
      toast.error(
        editingScholarship
          ? "Failed to update scholarship"
          : "Failed to create scholarship"
      );
    }
  };

  const handleEdit = useCallback((scholarship: Scholarship) => {
    setEditingScholarship(scholarship);
    setFormData({
      name: scholarship.name,
      description: scholarship.description ?? "",
      eligibility_criteria: scholarship.eligibility_criteria ?? "",
      amount: scholarship.amount?.toString() ?? "",
      application_deadline: scholarship.application_deadline ?? "",
      status: scholarship.status ?? "active",
    });
    setIsDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!confirm("Are you sure you want to delete this scholarship?")) {
        return;
      }

      try {
        const { error } = await supabase
          .from("scholarships")
          .delete()
          .eq("id", id);

        if (error) {
          throw error;
        }
        toast.success("Scholarship deleted successfully!");
        fetchScholarships();
      } catch (error: any) {
        console.error("Error deleting scholarship:", error);
        toast.error("Failed to delete scholarship");
      }
    },
    [fetchScholarships]
  );

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingScholarship(null);
    setFormData({
      name: "",
      description: "",
      eligibility_criteria: "",
      amount: "",
      application_deadline: "",
      status: "active",
    });
  };

  const filteredScholarships = scholarships.filter(scholarship => {
    const matchesSearch =
      scholarship.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scholarship.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || scholarship.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const tableColumns = useMemo<TableColumnDef<Scholarship>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) => rowIndex + 1,
      },
      {
        id: "name",
        label: "Scholarship Name",
        width: 200,
        renderCell: s => <span className="font-medium">{s.name}</span>,
      },
      {
        id: "amount",
        label: "Amount",
        width: 120,
        renderCell: s =>
          s.amount ? `₹${s.amount.toLocaleString("en-IN")}` : "N/A",
      },
      {
        id: "eligibility",
        label: "Eligibility",
        width: 200,
        renderCell: s => (
          <span className="max-w-xs truncate">
            {s.eligibility_criteria ?? "Not specified"}
          </span>
        ),
      },
      {
        id: "deadline",
        label: "Deadline",
        width: 120,
        renderCell: s => formatDate(s.application_deadline),
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: s => (
          <Badge
            variant={s.status === "active" ? "default" : "secondary"}
            className={
              s.status === "active"
                ? "border-success bg-success/10 text-success"
                : "bg-muted text-muted-foreground"
            }
          >
            {s.status ?? "N/A"}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 100,
        alwaysVisible: true,
        renderCell: s => (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleEdit(s)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDelete(s.id)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete]
  );

  const stats = {
    total: scholarships.length,
    active: scholarships.filter(s => s.status === "active").length,
    inactive: scholarships.filter(s => s.status === "inactive").length,
    totalAmount: scholarships.reduce((sum, s) => sum + (s.amount ?? 0), 0),
  };

  return (
    <DashboardLayout
      sidebarItems={sidebarItems}
      currentPath="/jainboarding-admin/scholarships"
      userName={user?.email ?? "Admin"}
      userPhone=""
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Scholarships</h2>
            <p className="text-muted-foreground">Manage scholarship programs</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="gap-2"
                onClick={() => setEditingScholarship(null)}
              >
                <Plus className="h-4 w-4" />
                Add Scholarship
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingScholarship
                    ? "Edit Scholarship"
                    : "Add New Scholarship"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Scholarship Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., Merit-Based Scholarship"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={e =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Enter scholarship description"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="eligibility">Eligibility Criteria</Label>
                  <Textarea
                    id="eligibility"
                    value={formData.eligibility_criteria}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        eligibility_criteria: e.target.value,
                      })
                    }
                    placeholder="Enter eligibility criteria"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount (₹)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={e =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      placeholder="e.g., 50000"
                      min="0"
                      step="1000"
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e =>
                        ["e", "E", "-", "+"].includes(e.key) &&
                        e.preventDefault()
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="deadline">Application Deadline</Label>
                    <Input
                      id="deadline"
                      type="date"
                      value={formData.application_deadline}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          application_deadline: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-red-500 text-white"
                    onClick={handleCloseDialog}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-500 text-white">
                    {editingScholarship ? "Update" : "Create"} Scholarship
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Scholarships
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                {stats.total}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Programs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-success text-3xl font-bold">
                {stats.active}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Inactive Programs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-muted-foreground">
                {stats.inactive}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Amount
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-maroon text-3xl font-bold">
                ₹{(stats.totalAmount / 100000).toFixed(1)}L
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
              <Input
                placeholder="Search scholarships..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Scholarships Table */}
        <Card>
          <CardContent className="p-3 pb-4">
            {loading ? (
              <div className="py-8 text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
                <p className="text-muted-foreground">Loading scholarships...</p>
              </div>
            ) : filteredScholarships.length === 0 ? (
              <div className="py-8 text-center">
                <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">No scholarships found</p>
                <p className="text-sm text-muted-foreground">
                  Click &quot;Add Scholarship&quot; to create your first
                  scholarship program
                </p>
              </div>
            ) : (
              <DataTable
                title={`Scholarship Programs (${filteredScholarships.length})`}
                tableId="hostel-scholarships"
                columns={tableColumns}
                data={filteredScholarships}
                getRowKey={s => s.id}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
